import { useState } from 'react'
import { AuthCard } from './AuthCard'
import { AuthInput } from './AuthInput'
import { AuthButton } from './AuthButton'
import { Lock, Save, ArrowLeft } from 'lucide-react'
import { AuthState } from '@/hooks/useCustomAuth'

interface ResetPasswordFormProps {
  authState: any
  handleResetPassword: (email: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: any }>
  changeState: (newState: AuthState, tempEmail?: string, fromLogin?: boolean) => void
}

export function ResetPasswordForm({
  authState,
  handleResetPassword,
  changeState
}: ResetPasswordFormProps) {
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!authState.tempEmail) return

    if (newPassword !== confirmPassword) {
      return
    }

    try {
      const result = await handleResetPassword(authState.tempEmail, code, newPassword)

      if (result?.success) {
        // The hook will handle state change to signIn
      }
    } catch (error) {
      console.error('Reset password error:', error)
    }
  }

  const isFormValid = () => {
    return code && newPassword && confirmPassword && newPassword === confirmPassword
  }

  return (
    <AuthCard
      title="Redefinir senha"
      subtitle={`Digite o código enviado para ${authState.tempEmail} e sua nova senha`}
      icon={<Lock className="h-8 w-8 text-brand-dark" />}
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
        <AuthInput
          label="Código de verificação"
          type="text"
          value={code}
          onChange={setCode}
          placeholder="Digite o código de 6 dígitos"
          required
          error={authState.error?.code === 'CodeMismatchException' ? authState.error.message : undefined}
          data-testid="reset-password-code-input"
        />

        <AuthInput
          label="Nova senha"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Digite sua nova senha"
          required
          error={authState.error?.code === 'InvalidPasswordException' ? authState.error.message : undefined}
          data-testid="reset-password-new-password-input"
        />

        <AuthInput
          label="Confirmar nova senha"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Digite a nova senha novamente"
          required
          error={confirmPassword && newPassword !== confirmPassword ? 'As senhas não coincidem' : undefined}
          data-testid="reset-password-confirm-password-input"
        />

        {authState.error && !['CodeMismatchException', 'InvalidPasswordException'].includes(authState.error.code || '') && (
          <div
            className="bg-error/20 border border-error text-error px-4 py-3 rounded-monumenta text-sm"
            data-testid="reset-password-error-message"
          >
            {authState.error.message}
          </div>
        )}

        <AuthButton
          type="submit"
          variant="primary"
          loading={authState.isLoading}
          disabled={!isFormValid()}
          className="w-full"
          icon={<Save className="h-5 w-5" />}
          data-testid="reset-password-submit-button"
        >
          Redefinir senha
        </AuthButton>

        <AuthButton
          variant="ghost"
          onClick={() => changeState('signIn')}
          className="w-full"
          icon={<ArrowLeft className="h-4 w-4" />}
          data-testid="reset-password-back-button"
        >
          Voltar ao login
        </AuthButton>
      </form>
    </AuthCard>
  )
}